import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

/**
 * PWA Install Prompt — shows a banner suggesting the user to install the app.
 * 
 * On Android Chrome: intercepts the beforeinstallprompt event.
 * On iOS Safari: shows manual instructions.
 * On Desktop: shows install button if available.
 * 
 * The banner is dismissible and remembers the user's choice for 7 days.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    if (standalone) return; // Already installed, don't show

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedAt < sevenDays) return;
    }

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    // Listen for beforeinstallprompt (Chrome/Edge/Samsung)
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // On iOS, show banner after 3 seconds (no beforeinstallprompt event)
    if (ios) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
  };

  if (!showBanner || isStandalone) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom md:left-auto md:right-4 md:max-w-sm">
      <div className="bg-surface-800/95 backdrop-blur-lg border border-surface-700 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
            <Smartphone size={24} className="text-white" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm">Instalar Finteck</h3>
            {isIOS ? (
              <p className="text-surface-400 text-xs mt-1">
                Toque em{' '}
                <span className="inline-flex items-center px-1 py-0.5 bg-surface-700 rounded text-surface-300">
                  ⬆ Compartilhar
                </span>{' '}
                e depois em{' '}
                <span className="inline-flex items-center px-1 py-0.5 bg-surface-700 rounded text-surface-300">
                  Adicionar à Tela de Início
                </span>
              </p>
            ) : (
              <p className="text-surface-400 text-xs mt-1">
                Acesse mais rápido direto da tela inicial do seu celular.
              </p>
            )}
          </div>

          {/* Close */}
          <button
            onClick={handleDismiss}
            className="text-surface-500 hover:text-surface-300 transition-colors p-1"
          >
            <X size={16} />
          </button>
        </div>

        {/* Action buttons */}
        {!isIOS && deferredPrompt && (
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleDismiss}
              className="flex-1 px-3 py-2 rounded-lg text-sm text-surface-400 hover:text-white hover:bg-surface-700 transition-colors"
            >
              Agora não
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors flex items-center justify-center gap-2"
            >
              <Download size={16} />
              Instalar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
